import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

export const FILTER_OPTION_RESOURCE = Object.freeze({
    AGENT: 'agent',
    CHANNEL: 'channel',
    CONTACT: 'contact',
    CREDENTIAL: 'credential',
    KNOWLEDGE_BASE: 'knowledge_base',
    USER: 'user'
});

const PAGE_SIZE = 100;

const getItems = (response) => {
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response)) return response;
    return [];
};

const loaders = {
    [FILTER_OPTION_RESOURCE.AGENT]: (search, signal) => {
        const { getAgents } = require('../../Agents/AgentServices');
        return getAgents({ page: 1, size: PAGE_SIZE, search, signal });
    },
    [FILTER_OPTION_RESOURCE.CHANNEL]: (search, signal) => {
        const { getChannels } = require('../../Channels/ChannelsServices');
        return getChannels({ page: 1, size: PAGE_SIZE, search, signal });
    },
    [FILTER_OPTION_RESOURCE.CONTACT]: (search, signal) => {
        const ContactsServices = require('../../Contacts/ContactsServices').default;
        return ContactsServices.getContacts({ page: 1, size: PAGE_SIZE, search, signal });
    },
    [FILTER_OPTION_RESOURCE.CREDENTIAL]: (search, signal) => {
        const { getCredentials } = require('../../Credentials/CredentialsServices');
        return getCredentials({ page: 1, size: PAGE_SIZE, search, signal });
    },
    [FILTER_OPTION_RESOURCE.KNOWLEDGE_BASE]: (search, signal) => {
        const { getknowledgeDatabases } = require('../../KnowledgeDatabase/KnowledgeDatabaseServices');
        return getknowledgeDatabases({ page: 1, size: PAGE_SIZE, search, signal });
    },
    [FILTER_OPTION_RESOURCE.USER]: (search, signal) => {
        const { getUsers } = require('../../Users/UsersServices');
        return getUsers({ page: 1, size: PAGE_SIZE, search_string: search, signal });
    }
};

const formatters = {
    [FILTER_OPTION_RESOURCE.AGENT]: (item) => item.name || item.id,
    [FILTER_OPTION_RESOURCE.CHANNEL]: (item) => item.name || item.external_channel_id || item.id,
    [FILTER_OPTION_RESOURCE.CONTACT]: (item) => (
        item.full_name ||
        [item.first_name, item.last_name].filter(Boolean).join(' ') ||
        item.external_contact_id ||
        item.email ||
        item.phone ||
        item.id
    ),
    [FILTER_OPTION_RESOURCE.CREDENTIAL]: (item) => item.name || item.email || item.credential_user_id || item.id,
    [FILTER_OPTION_RESOURCE.KNOWLEDGE_BASE]: (item) => item.name || item.id,
    [FILTER_OPTION_RESOURCE.USER]: (item) => (
        [item.first_name, item.last_name].filter(Boolean).join(' ') ||
        item.name ||
        item.email ||
        item.id
    )
};

const normalizeRelationConfig = (config) => {
    if (typeof config === 'string') {
        return { resource: config };
    }
    return config || {};
};

const toOptions = (resource, items) => (
    items
        .filter(item => item?.id !== undefined && item?.id !== null)
        .map(item => ({
            value: item.id,
            label: formatters[resource]?.(item) || item.id
        }))
);

const useFilterOptionFields = (fields, relationMap = {}, { handleError } = {}) => {
    const [optionsByField, setOptionsByField] = useState({});
    const debouncedSearchersRef = useRef({});
    const abortControllersRef = useRef({});

    const relationEntries = useMemo(() => {
        const fieldKeys = new Set(fields.map(field => field.key));
        return Object.entries(relationMap).filter(([fieldKey]) => fieldKeys.has(fieldKey));
    }, [fields, relationMap]);

    const loadOptions = useCallback(async (fieldKey, relationConfig, search = '') => {
        const config = normalizeRelationConfig(relationConfig);
        const loader = loaders[config.resource];
        if (!loader) return;

        if (abortControllersRef.current[fieldKey]) {
            abortControllersRef.current[fieldKey].abort();
        }

        const controller = new AbortController();
        abortControllersRef.current[fieldKey] = controller;

        try {
            const response = await loader(search, controller.signal);
            if (controller.signal.aborted) return;
            const options = toOptions(config.resource, getItems(response));
            setOptionsByField(prev => ({
                ...prev,
                [fieldKey]: options
            }));
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            if (handleError) {
                handleError(error);
            }
        }
    }, [handleError]);

    const getSearchHandler = useCallback((fieldKey, relationConfig) => {
        if (!debouncedSearchersRef.current[fieldKey]) {
            debouncedSearchersRef.current[fieldKey] = debounce((value) => {
                loadOptions(fieldKey, relationConfig, value);
            }, 500);
        }
        return debouncedSearchersRef.current[fieldKey];
    }, [loadOptions]);

    useEffect(() => {
        relationEntries.forEach(([fieldKey, config]) => {
            loadOptions(fieldKey, config);
        });
    }, [loadOptions, relationEntries]);

    useEffect(() => () => {
        Object.values(debouncedSearchersRef.current).forEach(searcher => searcher?.cancel?.());
        Object.values(abortControllersRef.current).forEach(controller => controller?.abort?.());
    }, []);

    return useMemo(() => fields.map(field => {
        const relationConfig = relationMap[field.key];
        if (!relationConfig) return field;

        const config = normalizeRelationConfig(relationConfig);
        const mode = config.mode || (field.type === 'enumMulti' ? 'multiple' : 'single');

        return {
            ...field,
            type: mode === 'multiple' ? 'enumMulti' : 'enum',
            options: optionsByField[field.key] || [],
            showSearch: true,
            onSearch: getSearchHandler(field.key, relationConfig),
            filterOption: false
        };
    }), [fields, getSearchHandler, optionsByField, relationMap]);
};

export default useFilterOptionFields;
